import produce, { setAutoFreeze } from 'immer';
import React, { useEffect, useMemo, useState } from 'react';
import { useController } from 'react-hook-form';
import { WidgetsRootContextProvider } from '../../../../../../tree/context/WidgetRootContext';
import { TreeNavigatorLogic } from '../../../../../../tree/navigation/TreeNavigatorLogic';
import {
  TreeReferencesContextProvider,
  useTreeReferencesContext,
} from '../../../../../../tree/references/TreeReferencesContext';
import { WidgetMainType, WidgetType } from '../../../../Widget';
import { useEngineReactComponentsContext } from '../../../../hooks/useEngineReactComponentsContext';
import { IRootWidget } from '../../../containers/root/IRootWidget';
import { ISubsectionWidget } from '../ISubsectionWidget';
import { SubsectionRendering } from '../SubsectionRendering';

// TODO Spostare in entrypoint dei progetti (storybook, validator, web)
setAutoFreeze(false);

export interface ISubsectionEditorProps {
  renderingKind: SubsectionRendering;
}

export function SubsectionEditor(props: ISubsectionEditorProps) {
  const { renderingKind } = props;
  const {
    field: { value, onChange, onBlur },
  } = useController({ name: 'children' });

  const { root: globalRoot, widget } = useTreeReferencesContext();

  const [root, setRoot] = useState<IRootWidget>({
    type: WidgetType.Root,
    mainType: WidgetMainType.Root,
    order: null,
    path: null,
    children: value ?? [],
  } as IRootWidget);

  // Aggiorno il form ogni volta che la root cambia
  useEffect(() => {
    onChange(root.children);
    onBlur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root]);

  const { sortableWidgetEditor: SortableWidgetEditor } =
    useEngineReactComponentsContext();

  // Abbiamo bisogno di utilizzare una root differente in qusto punto, per
  // poter inserire fra i riferimenti anche le variabili locali (della sottosezione).
  const widgetId = widget?.id;
  const updatedGlobalRoot = useMemo(() => {
    return produce(globalRoot, (draft: IRootWidget | undefined) => {
      if (!draft || !widgetId) return;
      const subsection = TreeNavigatorLogic.byId(
        draft,
        widgetId,
      ) as ISubsectionWidget;
      if (!subsection) return;
      subsection.children = root.children;
    });
  }, [globalRoot, root, widgetId]);

  return (
    <WidgetsRootContextProvider root={updatedGlobalRoot ?? null}>
      <TreeReferencesContextProvider root={updatedGlobalRoot} widget={widget}>
        <SortableWidgetEditor
          root={root}
          setRoot={setRoot}
          availableWidgetFilter={widgetType => {
            // Disabilitiamo la possibilità di inserire una sottosezione in una sottosezione
            // e un divisore di pagina in una sottosezione se la visualizzazione è inline
            if (
              renderingKind === SubsectionRendering.Inline &&
              widgetType.type === WidgetType.Page
            ) {
              return false;
            }

            return widgetType.type !== WidgetType.Subsection;
          }}
        />
      </TreeReferencesContextProvider>
    </WidgetsRootContextProvider>
  );
}
